/**
 * @file Hooks SWR de lecture et d'actions sur les notifications in-app d'un établissement.
 * @module hooks/api/app/Notifications
 *
 * Fournit l'accès aux notifications in-app avec pagination par curseur (cursor-based)
 * et polling automatique toutes les 30 secondes sur la première page.
 */

import {useState, useCallback} from 'react';
// useSWRInfinite est utilisé directement car useSWRWithSync wrappe uniquement useSWR (pagination classique).
// La pagination infinie (size/setSize, data en tableau de pages) est une primitive SWR distincte
// qui ne bénéficie pas de la synchro cross-tabs — la liste des notifs passées n'a pas besoin d'être
// synchronisée entre onglets. Le badge (useUnreadCount) utilise lui useSWRWithSync.
import useSWRInfinite from 'swr/infinite';
import {useSWRWithSync} from 'packages/common/hooks/useSWRWithSync';
import axiosInstance, {endpoints} from 'packages/common/utils/axios';
import {useSnackbar} from 'packages/common/components/snackbar';

/**
 * Nombre de notifications chargées par page
 *
 * @constant {number}
 */
const NOTIFICATION_PAGE_SIZE = 30;

/**
 * Hook SWR pour récupérer les notifications in-app avec chargement progressif
 *
 * Utilise une pagination par curseur (date `createdAt` de la dernière notification reçue)
 * pour éviter les doublons entre pages même si de nouvelles notifications arrivent.
 * La première page est rafraîchie automatiquement toutes les 30 secondes.
 * Les pages suivantes sont chargées à la demande via `loadMore`.
 *
 * @param {string|null} companyId - ID de l'établissement
 * @return {Object} Hook state
 * @return {Array} returns.notifications - Toutes les notifications chargées (toutes pages confondues)
 * @return {boolean} returns.isLoading - Chargement initial de la première page
 * @return {boolean} returns.isLoadingMore - Chargement d'une page supplémentaire en cours
 * @return {boolean} returns.isError - Erreur de chargement
 * @return {boolean} returns.hasMore - Indique s'il existe une page suivante à charger
 * @return {Function} returns.loadMore - Charge la page suivante
 * @return {Function} returns.mutate - Réinitialise à la page 1 et revalide
 */
export const useNotifications = (companyId) => {
  const getKey = (pageIndex, previousPageData) => {
    if (!companyId) return null;

    // Fin de la liste : la page précédente avait moins de résultats que la taille d'une page
    if (previousPageData && previousPageData.length < NOTIFICATION_PAGE_SIZE) return null;

    const baseUrl = endpoints.app.notifications.get(companyId);

    if (pageIndex === 0) return [baseUrl, null];

    // Le curseur est la date de création de la dernière notification de la page précédente
    const cursor = previousPageData[previousPageData.length - 1]?.createdAt;
    return [baseUrl, cursor];
  };

  const fetcher = ([url, before]) => {
    const params = before ? {before} : {};
    return axiosInstance.get(url, {params}).then((res) => res.data?.data || []);
  };

  const {data, error, mutate, size, setSize} = useSWRInfinite(getKey, fetcher, {
    refreshInterval: 30000,
    revalidateOnFocus: true,
    // Revalide uniquement la première page lors des polls automatiques
    revalidateFirstPage: true,
  });

  const notifications = data ? data.flat() : [];
  const lastPage = data?.[data.length - 1];
  const hasMore = !!lastPage && lastPage.length === NOTIFICATION_PAGE_SIZE;
  const isLoadingMore = size > 1 && !!data && data[size - 1] === undefined;

  const loadMore = useCallback(() => setSize((s) => s + 1), [setSize]);

  const resetAndMutate = useCallback(() => {
    setSize(1);
    mutate();
  }, [setSize, mutate]);

  return {
    notifications,
    isLoading: !data && !error && !!companyId,
    isLoadingMore,
    isError: !!error,
    hasMore,
    loadMore,
    mutate: resetAndMutate,
  };
};

/**
 * Hook SWR pour récupérer le nombre de notifications non lues
 *
 * Rafraîchissement automatique toutes les 30 secondes pour
 * maintenir le badge à jour sans charger toutes les notifications.
 *
 * @param {string|null} companyId - ID de l'établissement
 * @return {Object} Hook state
 * @return {number} returns.unreadCount - Nombre de notifications non lues
 * @return {boolean} returns.isLoading - État de chargement initial
 * @return {Function} returns.mutate - Fonction de revalidation SWR
 */
export const useUnreadCount = (companyId) => {
  const endpointUrl = companyId ? endpoints.app.notifications.unreadCount(companyId) : null;

  const fetcher = (url) => axiosInstance.get(url).then((res) => res.data?.data?.count ?? 0);

  const {data, error, mutate} = useSWRWithSync(endpointUrl, fetcher, {
    swrOptions: {
      refreshInterval: 30000,
      revalidateOnFocus: true,
    },
  });

  return {
    unreadCount: data ?? 0,
    isLoading: !error && data === undefined && !!companyId,
    mutate,
  };
};

/**
 * Hook pour basculer l'état lu/non lu d'une notification individuelle
 *
 * Applique une mise à jour optimiste immédiate dans le cache SWR avant la requête
 * serveur pour garantir une UX sans latence perceptible. En cas d'erreur,
 * SWR restaure automatiquement l'état précédent via revalidation.
 *
 * @param {Function} mutateNotifications - Fonction mutate du hook useNotifications pour invalider le cache
 * @param {Function} mutateUnreadCount - Fonction mutate du hook useUnreadCount pour invalider le badge
 * @return {Object} Hook state et actions
 * @return {Function} returns.toggleReadStatus - Fonction (companyId, notificationId, currentIsRead) => Promise
 * @return {boolean} returns.isLoading - Indique si une requête est en cours
 */
export const useToggleReadStatus = (mutateNotifications, mutateUnreadCount) => {
  const [isLoading, setLoading] = useState(false);

  const toggleReadStatus = useCallback(
      async (companyId, notificationId, currentIsRead) => {
        if (!companyId || !notificationId) return;

        setLoading(true);

        // Mise à jour optimiste : inverse localement isRead avant la réponse serveur
        mutateNotifications(
            (pages) =>
              pages?.map((page) =>
                page.map((n) => (n._id === notificationId ? {...n, isRead: !currentIsRead} : n)),
              ),
            {revalidate: false},
        );

        try {
          await axiosInstance.patch(endpoints.app.notifications.toggleRead(companyId, notificationId));
          mutateUnreadCount?.();
        } catch (_err) {
        // En cas d'erreur, revalide pour restaurer l'état réel depuis le serveur
          mutateNotifications();
        } finally {
          setLoading(false);
        }
      },
      [mutateNotifications, mutateUnreadCount],
  );

  return {toggleReadStatus, isLoading};
};

/**
 * Hook pour marquer toutes les notifications comme lues
 *
 * @return {Object} Hook state et actions
 * @return {Function} returns.markAllAsRead - Fonction pour tout marquer comme lu
 * @return {boolean} returns.isLoading - État de chargement
 */
export const useMarkAllAsRead = () => {
  const [isLoading, setLoading] = useState(false);
  const {enqueueSnackbar} = useSnackbar();

  const markAllAsRead = useCallback(
      async (companyId) => {
        if (!companyId) return;

        setLoading(true);

        try {
          await axiosInstance.put(endpoints.app.notifications.markAllRead(companyId));
        } catch (err) {
          enqueueSnackbar(err.response?.data?.message || 'Erreur lors de la mise à jour', {variant: 'error'});
        } finally {
          setLoading(false);
        }
      },
      [enqueueSnackbar],
  );

  return {markAllAsRead, isLoading};
};
